import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { productPriceLabel } from '../data/products';
import { useSite } from '../context/SiteContext';
import {
  getProductMeta,
  buildBreadcrumbJsonLd,
  buildProductJsonLd,
} from '../config/seo';
import * as publicApi from '../api/publicClient';
import OptimizedImage from '../components/OptimizedImage';
import SeoHead from '../components/SeoHead';
import './CategoryListing.css';
import './ProductDetail.css';

function ProductImage({ src, alt, onOpenZoom }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div
        className="product-detail__img-placeholder"
        aria-hidden
      />
    );
  }
  return (
    <button
      type="button"
      className="product-detail__img-btn"
      onClick={onOpenZoom}
      aria-label={`${alt} — büyüt`}
    >
      <OptimizedImage
        src={src}
        alt={alt}
        className="product-detail__img"
        loading="eager"
        decoding="async"
        onError={() => setFailed(true)}
      />
      <span className="product-detail__img-hint">Görseli büyüt</span>
    </button>
  );
}

function ImageLightbox({ src, alt, open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !src) return null;

  return (
    <div
      className="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Ürün görseli"
    >
      <button
        type="button"
        className="image-lightbox__backdrop"
        aria-label="Kapat"
        onClick={onClose}
      />
      <div className="image-lightbox__frame">
        <button
          type="button"
          className="image-lightbox__close"
          onClick={onClose}
        >
          Kapat
        </button>
        <img src={src} alt={alt} className="image-lightbox__img" />
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug, productId } = useParams();
  const { getWhatsAppUrl } = useSite();
  const { pathname } = useLocation();
  const isSekerlik = pathname.startsWith('/sekerlik');
  const hubPath = isSekerlik ? '/sekerlik' : '/davetiyeler';
  const hubKey = isSekerlik ? 'sekerlik' : 'davetiyeler';

  const [category, setCategory] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const closeZoom = useCallback(() => setZoomOpen(false), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setNotFound(false);
      try {
        const categories = await publicApi.getCategories(hubKey);
        if (cancelled) return;
        const match = categories.find((c) => c.slug === slug);
        if (!match) {
          setNotFound(true);
          return;
        }
        setCategory(match);
        const item = await publicApi.getProduct(hubKey, slug, productId);
        if (!cancelled) setProduct(item);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [hubKey, slug, productId]);

  if (loading) {
    return (
      <main className="page product-detail-page">
        <p className="section-note" style={{ padding: '2rem 1rem' }}>Yükleniyor…</p>
      </main>
    );
  }

  if (notFound || !category || !product) {
    return <Navigate to={`${hubPath}/${slug}`} replace />;
  }

  const priceText = productPriceLabel(product);
  const whatsappHref = getWhatsAppUrl();
  const waMsg = `Merhaba, "${product.name}" (${category.title}) hakkında bilgi almak istiyorum.`;
  const productWhatsApp = whatsappHref ? getWhatsAppUrl(waMsg) : null;
  const path = `${hubPath}/${slug}/${productId}`;
  const meta = getProductMeta(product, category);
  const hubLabel = isSekerlik ? 'Şekerlik' : 'Davetiyeler';
  const jsonLd = [
    buildBreadcrumbJsonLd([
      { name: 'Anasayfa', path: '/' },
      { name: hubLabel, path: hubPath },
      { name: category.shortTitle || category.title, path: `${hubPath}/${slug}` },
      { name: product.name, path },
    ]),
    buildProductJsonLd(product, category, path),
  ];

  return (
    <main className="page product-detail-page">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path={path}
        image={product.image}
        jsonLd={jsonLd}
      />
      <nav className="breadcrumb" aria-label="Sayfa konumu">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden> / </span>
        <Link to={hubPath}>{isSekerlik ? 'Şekerlik' : 'Davetiyeler'}</Link>
        <span aria-hidden> / </span>
        <Link to={`${hubPath}/${slug}`}>{category.shortTitle}</Link>
        <span aria-hidden> / </span>
        <span>{product.name}</span>
      </nav>

      <article className="product-detail section">
        <div className="product-detail__media">
          <ProductImage
            src={product.image}
            alt={product.name}
            onOpenZoom={() => setZoomOpen(true)}
          />
        </div>
        <div className="product-detail__body">
          <h1 className="product-detail__title">{product.name}</h1>
          {product.hint ? (
            <p className="product-detail__hint">{product.hint}</p>
          ) : null}
          <div className="product-detail__meta">
            {priceText ? (
              <p className="product-detail__price">{priceText}</p>
            ) : (
              <p className="product-detail__price product-detail__price--muted">
                Fiyat için iletişime geçin
              </p>
            )}
            {product.priceNote ? (
              <p className="product-detail__price-note">{product.priceNote}</p>
            ) : null}
          </div>
          <p className="product-detail__note">
            Fiyatlar yönlendiricidir; güncel teyit ve sipariş için WhatsApp ile
            iletişime geçebilirsiniz.
          </p>
          <div className="product-detail__actions">
            {productWhatsApp ? (
              <a
                href={productWhatsApp}
                className="product-detail__cta product-detail__cta--primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Bilgi / sipariş (WhatsApp)
              </a>
            ) : null}
            <Link
              to={`${hubPath}/${slug}`}
              className="product-detail__cta product-detail__cta--ghost"
            >
              Kategoriye dön
            </Link>
          </div>
        </div>
      </article>

      <ImageLightbox
        src={product.image}
        alt={product.name}
        open={zoomOpen}
        onClose={closeZoom}
      />
    </main>
  );
}
